<template>
  <content-subpage
    class="lesson-forum-topic-resource-edit-subpage"
    :visible="visible"
    :has-parent-subpage="hasParentSubpage"
    :titles="titles"
    @content-subpage-hidden="actionHidden"
    @content-subpage-back="actionBack"
  >
    <template #header-buttons>
      <fetch-data-button
        :kind="dataSaveButton.kind"
        :size="dataSaveButton.size"
        :loading="dataSaveButton.loading"
        :completed="dataSaveButton.completed"
        :error="dataSaveButton.error"
        :icon-only="dataSaveButton.iconOnly"
        :tip-position="dataSaveButton.tipPosition"
        :tip-alignment="dataSaveButton.tipAlignment"
        :error-message="dataSaveButton.errorMessage"
        @click="handleSaveReview"
        @reset-error-state="handleSaveButtonResetError"
      >
        <svg-icon :name="dataSaveButton.iconName" kind="action" />
      </fetch-data-button>
    </template>
    <template #main-content>
      <cv-form class="create-review" @submit.prevent>
        <dl class="field-set info">
          <dt class="field-label">
            {{ t("teacher_reviews_subpage_create_review_message_label") }}
          </dt>
          <dd class="field-value">
            <rich-text-editor
              ref="bodyRichTextEditor"
              class="body"
              kind="teacher_tinymce_4"
              size="large"
              :is-mandatory="true"
              :initial-content="review"
              :disabled="isInputDisabled"
              :text-id="dirtyId"
              @change="handleReviewChange"
            />
          </dd>
        </dl>
      </cv-form>
    </template>
  </content-subpage>
</template>

<script setup lang="ts">
import LessonReviewApi from "@apis/lesson.review.topic.api";
import { FORUM_TOPIC_RESOURCE_TYPE } from "@modules/lesson-review/lesson.topic.constant.ts";
import { ApiResponse } from "@apis/api.ts";
import { ForumTopicResourceModel } from "@modules/lesson-review/lesson-review-types.ts";
import { computed, onUnmounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import FetchDataButton from "@plugins/common/components/FetchDataButton.vue";
import ContentSubpage from "@plugins/content-subpage/components/ContentSubpage.vue";
import RichTextEditor from "@plugins/richtext/components/RichTextEditor.vue";
import { useContentSubpage } from "@plugins/content-subpage/composables/content-subpage.ts";
import { useProcessingState } from "@modules/app/composables/processing-state.ts";
import { useErrorHandler } from "@plugins/common/composables/error.ts";
import SvgIcon from "@plugins/common/components/SvgIcon.vue";

// Props typing
interface Params {
  topic: { type: string };
  topicResource: { uuid: string; content: string | undefined; rt_content: string | undefined };
  uuid: string;
  editResourceActionBack: (response: ForumTopicResourceModel) => void;
}
const props = defineProps<{ params: Params }>();

// i18n
const { t } = useI18n();

// State variables
const bodyRichTextEditor = ref();
const review = ref<string>();
const visible = ref(true);
const parentSubpageName = ref<string>("");
const paramsData = ref({ ...props.params });

// Computed properties
const processingId = computed<string>(() => props.params.uuid);
const dirtyId = computed<string>(() => paramsData.value.uuid);

// Mixin composition
const { resetProcessing } = useProcessingState(processingId.value);
const {
  hasParentSubpage,
  titles,
  actionHidden,
  actionBack,
  removeDirty,
  setDirty,
  dataSaveButton,
  defaultExpose,
} = useContentSubpage({ id: dirtyId });
const { passiveApiFailHandler } = useErrorHandler();

dataSaveButton.value = {
  completed: false,
  error: false,
  loading: false,
  iconOnly: true,
  tipPosition: "bottom",
  tipAlignment: "end",
  errorMessage: "",
  iconName: "Save24",
  kind: undefined,
  size: undefined,
};

const isInputDisabled = computed<boolean>(
  () => dataSaveButton.value.loading || dataSaveButton.value.completed,
);

// Methods
function handleReviewChange(newValue: string) {
  setDirty(dirtyId.value);
  review.value = newValue;
}

function validate() {
  if (!bodyRichTextEditor.value) {
    return true;
  }
  return bodyRichTextEditor.value.validate();
}

function saveForm() {
  paramsData.value.topicResource = {
    ...paramsData.value.topicResource,
    rt_content: review.value,
    content: undefined,
  };
  const inputParams = {
    postModel: paramsData.value.topicResource,
    uuid: paramsData.value.topicResource.uuid,
  };
  let editAction: (params: unknown) => Promise<ApiResponse<ForumTopicResourceModel>>;
  if (
    paramsData.value.topic.type === FORUM_TOPIC_RESOURCE_TYPE.LESSON_TEACHER_REVIEW ||
    paramsData.value.topic.type === FORUM_TOPIC_RESOURCE_TYPE.MOE_LIBRARY_TEACHER_REVIEW
  ) {
    editAction = LessonReviewApi.editLessonTeacherReviewTopicResource;
  } else {
    editAction = LessonReviewApi.editLessonAdminReviewTopicResource;
  }
  return new Promise(() => {
    editAction(inputParams).then((response: ApiResponse<ForumTopicResourceModel>) => {
      if (response.code !== "ok") {
        passiveApiFailHandler();
        handleSaveButtonResetError();
      } else {
        handleSuccessEdit(response.data);
      }
    }, passiveApiFailHandler);
  });
}

function handleSuccessEdit(response: ForumTopicResourceModel) {
  removeDirty(dirtyId.value);
  dataSaveButton.value.loading = false;
  dataSaveButton.value.completed = true;
  actionBack();
  paramsData.value.editResourceActionBack(response);
}

function handleSaveReview() {
  dataSaveButton.value.loading = true;
  if (validate()) {
    saveForm();
  } else {
    handleSaveButtonError();
  }
}

function handleSaveButtonError() {
  dataSaveButton.value.loading = false;
  dataSaveButton.value.error = true;
}

function handleSaveButtonResetError() {
  dataSaveButton.value.loading = false;
  dataSaveButton.value.error = false;
}

// Lifecycle
parentSubpageName.value = titles.value.slice(-1)[0];
titles.value.push(t("teacher_reviews_subpage_edit_review_title"));
review.value = props.params.topicResource.content;

onUnmounted(() => {
  resetProcessing();
  titles.value.pop();
});

defineExpose(defaultExpose);
</script>

<style lang="scss">
.lesson-forum-topic-resource-edit-subpage {
  // Min-height to give sufficient space to prevent the action buttons from being cut off
  .main {
    min-height: 30rem;
  }
}
</style>
